import { useEnabledFeatures, useQuery, useTeam } from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import type {
  accountAssignmentType_enum,
  accountClassification_enum,
} from '@vrplatform/graphql';
import {
  formatAssignmentType,
  getAssignmentTypeDescription,
} from '@vrplatform/ui-common';

const PAYOUT_BANK_ACCOUNT_CLASSIFICATION_MESSAGE = `${formatAssignmentType('payout_bankAccount')} is only available on Asset or Liability accounts.`;

export function getAssignmentDisabledMessage({
  type,
  classification,
}: {
  classification?: accountClassification_enum;
  type: accountAssignmentType_enum;
}) {
  if (
    type === 'payout_bankAccount' &&
    classification !== 'asset' &&
    classification !== 'liability'
  ) {
    return PAYOUT_BANK_ACCOUNT_CLASSIFICATION_MESSAGE;
  }

  return undefined;
}

export function useAccountsAssignmentsQuery({
  classification,
  currentAccountAssignments,
  skip,
}: {
  classification?: accountClassification_enum;
  skip: boolean;
  currentAccountAssignments: accountAssignmentType_enum[];
}) {
  const [{ id: teamId }] = useTeam();
  const { OPEX } = useEnabledFeatures();

  return useQuery(
    (q, args) => {
      const existingAssignments = q
        .accountAssignments({
          where: {
            tenantId: { _eq: args.teamId },
          },
          order_by: [{ type: 'asc' }],
        })
        .map((assignment) => assignment.type);

      return q
        .accountAssignmentTypes({
          order_by: [{ name: 'asc' }],
          where: args.OPEX
            ? undefined
            : {
                name: { _neq: 'dueToFromTrust' }, // do not require dueToFromTrust for non opex tenants
              },
        })
        .map<SelectItem<accountAssignmentType_enum>>((x) => {
          const type = x.name! as accountAssignmentType_enum;
          const disabledByMessage = getAssignmentDisabledMessage({
            type,
            classification: args.classification,
          });

          const disabledByExistingAssignment =
            existingAssignments.includes(type) &&
            !args.currentAccountAssignments.includes(type);

          return {
            value: type,
            label: formatAssignmentType(type),
            disabled: !!(disabledByExistingAssignment || disabledByMessage),
            description:
              disabledByMessage ||
              (disabledByExistingAssignment
                ? 'Already assigned to another account'
                : getAssignmentTypeDescription(type)),
          };
        });
    },
    {
      skip,
      queryKey: ['accounts', 'assignmentTypes'],
      variables: {
        classification,
        teamId,
        currentAccountAssignments,
        OPEX,
      },
    }
  );
}
